import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Add_DashSearchToStack } from "@mirage/analytics/events/types/add_dash_search_to_stack";
import { PAP_Copy_DashSearch } from "@mirage/analytics/events/types/copy_dash_search";
import { PAP_Open_DashSearchResult } from "@mirage/analytics/events/types/open_dash_search_result";
import { PAP_Shown_DashSearchResult } from "@mirage/analytics/events/types/shown_dash_search_result";
import { getTypeaheadFilterTitle } from "@mirage/mosaics/SearchBarWithTypeahead/typeahead-search-filter-result";
import { stackDerivePAPProps } from "@mirage/service-stacks";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { concatActiveFilterBindings } from "@mirage/shared/search/search-filters";
import { isDesktopLocalFile } from "@mirage/shared/search/search-result";
import { useCallback } from "react";
import { isResultVerified } from "../curations";
import useSearchQueryId from "./useSearchQueryId";

import type { stacks } from "@dropbox/api-v2-client";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { DashSearchResultType } from "@mirage/analytics/events/enums/dash_search_result_type";
import type { DashSourceType } from "@mirage/analytics/events/enums/dash_source_type";
import type { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import type { LaunchMethod } from "@mirage/analytics/events/enums/launch_method";
import type { OmniboxDropdownSection } from "@mirage/analytics/events/enums/omnibox_dropdown_section";
import type { OmniboxFocusMode } from "@mirage/analytics/events/enums/omnibox_focus_mode";
import type { TypeaheadResultType } from "@mirage/analytics/events/enums/typeahead_result_type";
import { PAP_Click_DashNewStack } from "@mirage/analytics/events/types/click_dash_new_stack";
import type { Open_DashSearchResult } from "@mirage/analytics/events/types/open_dash_search_result";
import { SessionManagerName } from "@mirage/analytics/session/session-manager";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type {
  AskUserQuery,
  DraftFromTemplateResult,
  DraftFromToneResult,
  MathCalculation,
  OrganizeUserQuery,
  Skeleton,
  WriteUserQuery,
} from "@mirage/service-typeahead-search/service/types";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import type { URLShortcut } from "@mirage/shared/search/url-shortcut";
import type { PeopleRecommendation } from "../search/people-recommendations";
import type { RecentChat } from "../search/recent-chats";
import {
  isOmniboxRecommendation,
  type Recommendation,
} from "../search/recommendation";
import type { StackItem } from "../search/stack-item";

export type DefaultProps = {
  query: string;
  filters: Array<SearchFilter>;
  results: SearchResult[] | typeahead.ScoredResult[];
  featureLine: FeatureLine;
  omniboxFocusMode?: OmniboxFocusMode;
  omniboxDropdownSection?: OmniboxDropdownSection;

  // Optional Params
  dashActionSurface?: DashActionSurface;
  actionSurfaceComponent?: ActionSurfaceComponent;
};

export type SearchResultDefaultProps = DefaultProps & {
  searchResult: SearchResult;
};

export type TypeaheadResultDefaultProps = DefaultProps & {
  result: typeahead.ScoredResult;
};

export type StackResultDefaultProps = DefaultProps & {
  uuid: string;
  stack: stacks.Stack;
};

export type StackItemResultDefaultProps = DefaultProps & {
  stackItem: StackItem;
};

export type PreviousQueryDefaultProps = DefaultProps & {
  previousQuery: PreviousQuery;
};

export type RecommendationDefaultProps = DefaultProps & {
  recommendation: Recommendation;
};

export type URLShortcutDefaultProps = DefaultProps & {
  urlShortcut: URLShortcut;
};

export type MathCalculationDefaultProps = DefaultProps & {
  mathCalculation: MathCalculation;
};

export type DesktopFileDefaultProps = DefaultProps & {
  desktopFile: SearchResult;
};

export type DesktopApplicationDefaultProps = DefaultProps & {
  desktopApplication: SearchResult;
};

export type SearchFilterDefaultProps = DefaultProps & {
  searchFilter: SearchFilter;
};

export type AskSuggestionDefaultProps = DefaultProps & {
  askSuggestion: QuerySuggestionResult;
};

export type AskUserQueryDefaultProps = DefaultProps & {
  askUserQuery: AskUserQuery;
};

export type RecentChatDefaultProps = DefaultProps & {
  recentChat: RecentChat;
};

export type DraftWithSourceDefaultProps = DefaultProps & {
  draftWithSource: Recommendation;
};

export type OrganizeUserQueryDefaultProps = DefaultProps & {
  organizeUserQuery: OrganizeUserQuery;
};

export type DraftFromTemplateDefaultProps = DefaultProps & {
  draftFromTemplate: DraftFromTemplateResult;
};

export type DraftFromToneDefaultProps = DefaultProps & {
  draftFromTone: DraftFromToneResult;
};

export type WriteUserQueryDefaultProps = DefaultProps & {
  writeUserQuery: WriteUserQuery;
};

export type PeopleResultDefaultProps = DefaultProps & {
  peopleResult: PeopleRecommendation;
};

export type SkeletonDefaultProps = DefaultProps & {
  skeleton: Skeleton;
};

export function useResultPAPLogger(
  { isTypeahead }: { isTypeahead: boolean } = { isTypeahead: true },
) {
  const { reportPapEvent, reportSessionAwarePapEvent, withActionSurface } =
    useMirageAnalyticsContext();
  const { searchQueryUuid } = useSearchQueryId();

  const logSearchResultOpen = useCallback(
    ({
      defaultProps,
      rightRailOpened,
      launchMethod,
      actionSurfaceComponent,
      typeaheadResultType,
      typeaheadScore,
      typeaheadScoringNotes,
      dashSourceType = "connector",
      categorizedResult = false,
    }: {
      defaultProps: SearchResultDefaultProps;
      rightRailOpened: boolean;
      launchMethod: LaunchMethod;
      actionSurfaceComponent?: ActionSurfaceComponent;
      typeaheadResultType?: TypeaheadResultType;
      typeaheadScore?: number;
      typeaheadScoringNotes?: typeahead.ScoringNotes;
      dashSourceType?: DashSourceType;
      categorizedResult?: boolean;
    }) => {
      const {
        results,
        searchResult,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: searchResult.uuid,
            connector: searchResult?.connectorInfo?.connectorName,
            activeFilters,
            queryString: query,
            title: searchResult.title ? searchResult.title : "",
            dashSearchResultType: searchResult.recordType?.[".tag"],
            searchRequestId: searchResult.searchRequestId,
            dashSourceType,
            resultUpdatedAtMs: searchResult.updatedAtMs ?? undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : searchResult.score,
            hitCount: undefined,
            resultCount,
            searchResultSource: searchResult.searchResultSource,
            fromUpstream: searchResult.searchResultSource === "upstream",
            isPinned: false,
            latency: searchResult.latency ?? undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent: categorizedResult
              ? "serp_categories"
              : actionSurfaceComponent,
            searchQueryUuid,
            serpFinalRanking: searchResult.serpFinalRanking,
            dashIsResultVerified: isResultVerified(searchResult),
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            categorizedResult,
            fileTypeCategory: searchResult.fileTypeInfo?.id,
            omniboxFocusMode,
            omniboxDropdownSection,
            hasOcrText: !!searchResult.ocrResult?.ocrText,
            ...sessionAwareness,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.searchResult.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logStackResultOpen = useCallback(
    (
      defaultProps: StackResultDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "stack",
    ) => {
      const {
        results,
        stack,
        query,
        filters,
        uuid,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: stack?.stack_data?.name,
            resultUuid: uuid,
            activeFilters,
            queryString: query,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            isPinned: false,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            featureLine,
            launchMethod,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logStackItemResultOpen = useCallback(
    (
      defaultProps: StackItemResultDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "stack",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        stackItem,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: stackItem.name,
            resultUuid: stackItem.uuid,
            activeFilters,
            queryString: query,
            dashSourceType,
            searchResultSource: "local_cache",
            launchMethod,
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            isPinned: false,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            featureLine,
            // Typeheaed-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.stackItem.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logPreviousQueryOpen = useCallback(
    (
      defaultProps: PreviousQueryDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        previousQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: previousQuery.query,
            activeFilters,
            queryString: query,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            launchMethod,
            searchQueryUuid,
            featureLine,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.previousQuery.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logRecommendationOpen = useCallback(
    (
      defaultProps: RecommendationDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        recommendation,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;

      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: recommendation.uuid,
            connector: recommendation?.connectorInfo?.connectorName,
            activeFilters,
            queryString: query,
            title: recommendation.title ? recommendation.title : "",
            dashSearchResultType: recommendation.recordType?.[".tag"],
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            typeaheadRecommendationRequestId:
              recommendation.typeaheadRecommendationRequestId ?? undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: isOmniboxRecommendation(recommendation)
              ? recommendation.predictionId
              : undefined,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.recommendation.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logURLShortcutOpen = useCallback(
    (
      defaultProps: URLShortcutDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "url_shortcut",
    ) => {
      const {
        results,
        query,
        filters,
        urlShortcut,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: urlShortcut.uuid,
            connector: undefined,
            activeFilters,
            queryString: query,
            title: urlShortcut.parameters.activeHotword,
            dashSearchResultType: "url_shortcut",
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.urlShortcut.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logMathCalculationOpen = useCallback(
    (
      defaultProps: MathCalculationDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        mathCalculation,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: mathCalculation.uuid,
            connector: undefined,
            activeFilters,
            queryString: query,
            title: mathCalculation.answer,
            dashSearchResultType: "math",
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.mathCalculation.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSearchFilterOpen = useCallback(
    (
      defaultProps: SearchFilterDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        searchFilter,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const title = getTypeaheadFilterTitle(searchFilter);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            connector: undefined,
            activeFilters,
            queryString: query,
            title: title,
            sourceType: searchFilter.type,
            dashSearchResultType: "filter",
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid: searchQueryUuid.length
              ? searchQueryUuid
              : undefined,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.searchFilter.id,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logAskSuggestionOpen = useCallback(
    (
      defaultProps: AskSuggestionDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        featureLine,
        askSuggestion,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.askSuggestion.uuid,
      );
      const title = askSuggestion.text;
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            connector: undefined,
            queryString: query,
            title: title,
            dashSearchResultType: "other",
            searchRequestId: undefined,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid: searchQueryUuid.length
              ? searchQueryUuid
              : undefined,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: askSuggestion.predictionId,
            assistRequestId: askSuggestion.assistRequestId,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logAskUserQueryOpen = useCallback(
    (
      defaultProps: AskUserQueryDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        askUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.askUserQuery.uuid,
      );
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          withActionSurface((actionSurfaceResult) =>
            PAP_Open_DashSearchResult({
              title: askUserQuery.query,
              activeFilters,
              queryString: query,
              resultPosition,
              resultPositionNoCta,
              dashSourceType,
              searchResultSource: "local_cache",
              rightRailOpened,
              hitCount: undefined,
              resultCount,
              fromUpstream: false,
              isPinned: false,
              totalScore: isTypeahead ? typeaheadScore : undefined,
              hasQuery,
              dashActionSurface,
              actionSurfaceComponent,
              launchMethod,
              searchQueryUuid,
              featureLine,
              // Typeahead-specific
              isTypeahead,
              typeaheadResultType: isTypeahead
                ? typeaheadResultType
                : undefined,
              omniboxFocusMode,
              omniboxDropdownSection,
              ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
              ...actionSurfaceResult,
              ...sessionAwareness,
            }),
          ),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logWriteUserQueryOpen = useCallback(
    (
      defaultProps: WriteUserQueryDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        writeUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.writeUserQuery.uuid,
      );
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: writeUserQuery.query,
            activeFilters,
            queryString: query,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            launchMethod,
            searchQueryUuid,
            featureLine,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logOrganizeUserQueryOpen = useCallback(
    (
      defaultProps: OrganizeUserQueryDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        organizeUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.organizeUserQuery.uuid,
      );
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: organizeUserQuery.query,
            activeFilters,
            queryString: query,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            launchMethod,
            searchQueryUuid,
            featureLine,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );

      reportPapEvent(
        PAP_Click_DashNewStack({
          actionSurfaceComponent: "omnibox",
          featureLine: "stacks",
        }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logRecentChatOpen = useCallback(
    (
      defaultProps: RecentChatDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        recentChat,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.recentChat.uuid,
      );
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            title: recentChat.title,
            activeFilters,
            queryString: query,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened,
            hitCount: undefined,
            resultCount,
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            launchMethod,
            searchQueryUuid,
            featureLine,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftWithSourceOpen = useCallback(
    (
      defaultProps: DraftWithSourceDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftWithSource,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.draftWithSource.uuid,
      );
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: draftWithSource.uuid,
            connector: draftWithSource?.connectorInfo?.connectorName,
            activeFilters,
            queryString: query,
            title: draftWithSource.title ? draftWithSource.title : "",
            dashSearchResultType: draftWithSource.recordType?.[".tag"],
            searchRequestId: undefined,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            typeaheadRecommendationRequestId:
              draftWithSource.typeaheadRecommendationRequestId ?? undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftFromTemplateOpen = useCallback(
    (
      defaultProps: DraftFromTemplateDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftFromTemplate,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.draftFromTemplate.uuid,
      );
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: draftFromTemplate.uuid,
            activeFilters,
            queryString: query,
            title: draftFromTemplate.title ? draftFromTemplate.title : "",
            dashSearchResultType: "canned",
            searchRequestId: undefined,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftFromToneOpen = useCallback(
    (
      defaultProps: DraftFromToneDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftFromTone,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.draftFromTone.uuid,
      );
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: draftFromTone.uuid,
            activeFilters,
            queryString: query,
            title: draftFromTone.title ? draftFromTone.title : "",
            dashSearchResultType: "canned",
            searchRequestId: undefined,
            resultPosition,
            resultPositionNoCta,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logPeopleResultOpen = useCallback(
    (
      defaultProps: PeopleResultDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSearchResultType?: DashSearchResultType,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        peopleResult,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: peopleResult.uuid,
            activeFilters,
            queryString: query,
            title: peopleResult.displayName ?? peopleResult.email,
            dashSearchResultType,
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: peopleResult.predictionId,
            ...sessionAwareness,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.peopleResult.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSkeletonOpen = useCallback(
    (
      defaultProps: SkeletonDefaultProps,
      rightRailOpened: boolean,
      launchMethod: LaunchMethod,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSearchResultType?: DashSearchResultType,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        skeleton,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Open_DashSearchResult({
            resultUuid: skeleton.uuid,
            activeFilters,
            queryString: query,
            title: skeleton.proxyType,
            dashSearchResultType,
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            launchMethod,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...sessionAwareness,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.skeleton.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSearchResultShown = useCallback(
    (
      defaultProps: SearchResultDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "connector",
    ) => {
      const {
        results,
        searchResult,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: searchResult.title,
            resultUuid: searchResult.uuid,
            connector: searchResult?.connectorInfo?.connectorName,
            totalScore: isTypeahead ? typeaheadScore : searchResult.score,
            sourceScore: searchResult.score ?? 0,
            searchResultSource: searchResult.searchResultSource,
            fromUpstream: searchResult.searchResultSource === "upstream",
            resultUpdatedAtMs: searchResult.providerUpdateAtMs || undefined,
            dashSearchResultType: searchResult.recordType?.[".tag"],
            searchRequestId: searchResult.searchRequestId,
            resultCount,
            rightRailOpened: false,
            dashSourceType: dashSourceType,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            featureLine,
            searchQueryUuid,
            serpFinalRanking: searchResult.serpFinalRanking,
            dashIsResultVerified: isResultVerified(searchResult),
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            fileTypeCategory: searchResult.fileTypeInfo?.id,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.searchResult.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
            hasOcrText: !!searchResult.ocrResult?.ocrText,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logRecommendationShown = useCallback(
    (
      defaultProps: RecommendationDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        recommendation,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: recommendation.title ? recommendation.title : "",
            resultUuid: recommendation.uuid,
            connector: recommendation?.connectorInfo?.connectorName,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            resultUpdatedAtMs: undefined,
            dashSearchResultType: recommendation.recordType?.[".tag"],
            resultCount,
            rightRailOpened: false,
            dashSourceType,
            hasQuery,
            dashActionSurface,
            actionSurfaceComponent,
            featureLine,
            searchQueryUuid,
            searchRequestId: undefined,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            typeaheadRecommendationRequestId:
              recommendation.typeaheadRecommendationRequestId ?? undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: isOmniboxRecommendation(recommendation)
              ? recommendation.predictionId
              : undefined,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.recommendation.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSearchResultCopy = useCallback(
    (defaultProps: SearchResultDefaultProps) => {
      const searchResult = defaultProps.searchResult as SearchResult;
      const { query, results } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.searchResult.uuid,
      );
      const dashConnectorId = searchResult.connectorInfo.connectorId;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Copy_DashSearch({
            queryString: query,
            resultRank: resultPosition,
            resultPositionNoCta,
            dashConnectorId,
            resultCount,
            dashSearchResultType: searchResult.recordType?.[".tag"],
            searchResultSource: searchResult.searchResultSource,
            searchRequestId: searchResult.searchRequestId,
            resultUuid: searchResult.uuid,
            searchQueryUuid,
            featureLine: "search",
            dashIsResultVerified: isResultVerified(searchResult),
            hasOcrText: !!searchResult.ocrResult?.ocrText,
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );
  const logTypeaheadResultCopy = useCallback(
    (
      defaultProps: TypeaheadResultDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      _typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
    ) => {
      const { result, query, results, featureLine } = defaultProps;
      const resultCount = results.length;
      const { resultPosition, resultPositionNoCta } = calculatePapPositions(
        results,
        (result) => result.uuid === defaultProps.result.uuid,
      );

      // NOTE: There's not a helper function like the open/shown events, this
      // is getting and reporting a raw PAP event
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Copy_DashSearch({
            queryString: query,
            resultRank: resultPosition,
            resultPositionNoCta,
            resultCount,
            resultUuid: result.uuid,
            searchQueryUuid,
            featureLine,
            actionSurfaceComponent,
            // NOTE: We would track `totalScore` here, but that PAP property isn't on the copy event!
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSearchResultAddedToStack_SERP = useCallback(
    (
      defaultProps: SearchResultDefaultProps,
      stack?: stacks.Stack,
      actionSurfaceComponent?: ActionSurfaceComponent,
    ) => {
      const { query, filters, results } = defaultProps;
      const searchResult = defaultProps.searchResult as SearchResult;
      const resultCount = results.length;
      const dashConnectorId = searchResult.connectorInfo.connectorId;
      const stackProps = stack ? { ...stackDerivePAPProps(stack) } : {};
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Add_DashSearchToStack({
            queryString: query,
            dashConnectorId,
            resultUuid: searchResult.uuid,
            connector: searchResult.connectorInfo?.connectorName,
            dashSearchResultType: searchResult.recordType?.[".tag"],
            searchResultSource: searchResult.searchResultSource,
            searchRequestId: searchResult.searchRequestId,
            resultCount,
            activeFilters,
            featureLine: "search",
            dashSourceType: isDesktopLocalFile(searchResult)
              ? "local_file_and_applications"
              : "connector",
            isSuccess: !!stack,
            actionSurfaceComponent,
            searchQueryUuid,
            dashIsResultVerified: isResultVerified(searchResult),
            ...calculatePapPositions(
              results,
              (result) => result.uuid === searchResult.uuid,
            ),
            ...stackProps,
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );
  const logSearchResultAddedToStack_Typeahead = useCallback(
    (
      defaultProps: TypeaheadResultDefaultProps,
      stack?: stacks.Stack,
      actionSurfaceComponent?: ActionSurfaceComponent,
    ) => {
      const {
        query,
        filters,
        results,
        result: scoredResult,
        featureLine,
      } = defaultProps;
      const resultCount = results.length;
      let dashConnectorId!: string,
        connector!: string,
        dashSearchResultType!: DashSearchResultType;

      if (
        scoredResult.type === typeahead.ResultType.SearchResult ||
        scoredResult.type === typeahead.ResultType.Recommendation
      ) {
        dashConnectorId = scoredResult.result.connectorInfo.connectorId;
        connector = scoredResult.result.connectorInfo.connectorName;
        dashSearchResultType = scoredResult.result.recordType?.[".tag"];
      }

      const stackProps = stack ? { ...stackDerivePAPProps(stack) } : {};
      const activeFilters = concatActiveFilterBindings(filters);
      const dashSourceType = [
        typeahead.ResultType.DesktopApplication,
        typeahead.ResultType.DesktopFile,
      ].includes(scoredResult.type)
        ? "local_file_and_applications"
        : "connector";

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Add_DashSearchToStack({
            queryString: query,
            dashConnectorId,
            resultUuid: scoredResult.uuid,
            connector,
            dashSearchResultType,
            resultCount,
            activeFilters,
            featureLine,
            dashSourceType,
            isSuccess: !!stack,
            actionSurfaceComponent,
            searchQueryUuid,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === scoredResult.uuid,
            ),
            ...stackProps,
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logStackResultShown = useCallback(
    (
      defaultProps: StackResultDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "stack",
    ) => {
      const {
        results,
        uuid,
        query,
        filters,
        stack,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;

      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: stack.stack_data?.name,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            fromUpstream: false,
            hasQuery,
            featureLine,
            actionSurfaceComponent,
            dashActionSurface,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(results, (result) => result.uuid === uuid),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logStackItemResultShown = useCallback(
    (
      defaultProps: StackItemResultDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "stack",
    ) => {
      const {
        results,
        stackItem,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: stackItem.name,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            fromUpstream: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.stackItem.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logPreviousQueryShown = useCallback(
    (
      defaultProps: PreviousQueryDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "other",
    ) => {
      const {
        results,
        previousQuery,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: previousQuery.query,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            fromUpstream: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.previousQuery.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logURLShortcutShown = useCallback(
    (
      defaultProps: URLShortcutDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "url_shortcut",
    ) => {
      const {
        results,
        urlShortcut,
        query,
        featureLine,
        filters,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: urlShortcut.parameters.activeHotword,
            resultUuid: urlShortcut.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            fromUpstream: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.urlShortcut.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logMathCalculationShown = useCallback(
    (
      defaultProps: MathCalculationDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        mathCalculation,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: mathCalculation.answer,
            resultUuid: mathCalculation.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            fromUpstream: false,
            dashSourceType,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.mathCalculation.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDesktopFileShown = useCallback(
    (
      defaultProps: DesktopFileDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local_file_and_applications",
    ) => {
      const {
        results,
        desktopFile,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: desktopFile.title,
            resultUuid: desktopFile.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            fromUpstream: false,
            dashSourceType,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.desktopFile.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSearchFilterShown = useCallback(
    (
      defaultProps: SearchFilterDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        searchFilter,
        query,
        filters,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);
      const title = getTypeaheadFilterTitle(searchFilter);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: title,
            sourceType: searchFilter.type,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            fromUpstream: false,
            hasQuery,
            featureLine,
            dashSourceType,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid: searchQueryUuid.length
              ? searchQueryUuid
              : undefined,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === searchFilter.id,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDesktopApplicationShown = useCallback(
    (
      defaultProps: DesktopApplicationDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local_file_and_applications",
    ) => {
      const {
        results,
        desktopApplication,
        filters,
        query,
        featureLine,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const hasQuery = query.length > 0;
      const activeFilters = concatActiveFilterBindings(filters);

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title: desktopApplication.title,
            resultUuid: desktopApplication.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            fromUpstream: false,
            hasQuery,
            featureLine,
            dashSourceType,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.desktopApplication.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logAskSuggestionShown = useCallback(
    (
      defaultProps: AskSuggestionDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        askSuggestion,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = askSuggestion.text;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title,
            resultUuid: askSuggestion.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            dashSearchResultType: "other",
            fromUpstream: false,
            isPinned: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: askSuggestion.predictionId,
            assistRequestId: askSuggestion.assistRequestId,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.askSuggestion.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logAskUserQueryShown = useCallback(
    (
      defaultProps: AskUserQueryDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        askUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = askUserQuery.query;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title,
            resultUuid: askUserQuery.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            dashSearchResultType: "other",
            fromUpstream: false,
            isPinned: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.askUserQuery.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logWriteUserQueryShown = useCallback(
    (
      defaultProps: WriteUserQueryDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        writeUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = writeUserQuery.query;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title,
            resultUuid: writeUserQuery.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            dashSearchResultType: "other",
            fromUpstream: false,
            isPinned: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.writeUserQuery.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logOrganizeUserQueryShown = useCallback(
    (
      defaultProps: OrganizeUserQueryDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        organizeUserQuery,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = organizeUserQuery.query;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title,
            resultUuid: organizeUserQuery.uuid,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            dashSearchResultType: "other",
            fromUpstream: false,
            isPinned: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.organizeUserQuery.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logRecentChatShown = useCallback(
    (
      defaultProps: RecentChatDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        recentChat,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = recentChat.title;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            title,
            activeFilters,
            dashSourceType,
            searchResultSource: "local_cache",
            rightRailOpened: false,
            queryString: query,
            resultUuid: recentChat.uuid,
            resultCount,
            fromUpstream: false,
            isPinned: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            dashSearchResultType: "other",
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.recentChat.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftWithSourceShown = useCallback(
    (
      defaultProps: DraftWithSourceDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftWithSource,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;
      const title = draftWithSource.title;
      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            activeFilters,
            queryString: query,
            title,
            resultUuid: draftWithSource.uuid,
            connector: draftWithSource.connectorInfo?.connectorName,
            resultCount,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            rightRailOpened: false,
            dashSourceType,
            searchResultSource: "local_cache",
            dashSearchResultType: draftWithSource.recordType?.[".tag"],
            fromUpstream: false,
            isPinned: false,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType: isTypeahead ? typeaheadResultType : undefined,
            typeaheadRecommendationRequestId:
              draftWithSource.typeaheadRecommendationRequestId ?? undefined,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.draftWithSource.uuid,
            ),
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftFromTemplateShown = useCallback(
    (
      defaultProps: DraftFromTemplateDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftFromTemplate,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            resultUuid: draftFromTemplate.uuid,
            activeFilters,
            queryString: query,
            title: draftFromTemplate.title ? draftFromTemplate.title : "",
            dashSearchResultType: "canned",
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.draftFromTemplate.uuid,
            ),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logDraftFromToneShown = useCallback(
    (
      defaultProps: DraftFromToneDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        draftFromTone,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            resultUuid: draftFromTone.uuid,
            activeFilters,
            queryString: query,
            title: draftFromTone.title ? draftFromTone.title : "",
            dashSearchResultType: "canned",
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.draftFromTone.uuid,
            ),
            ...sessionAwareness,
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logPeopleResultShown = useCallback(
    (
      defaultProps: PeopleResultDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSearchResultType?: DashSearchResultType,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        peopleResult,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            resultUuid: peopleResult.uuid,
            activeFilters,
            queryString: query,
            title: peopleResult.displayName ?? peopleResult.email,
            dashSearchResultType,
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            predictionId: peopleResult.predictionId,
            ...sessionAwareness,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.peopleResult.uuid,
            ),
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  const logSkeletonShown = useCallback(
    (
      defaultProps: SkeletonDefaultProps,
      actionSurfaceComponent?: ActionSurfaceComponent,
      typeaheadResultType?: TypeaheadResultType,
      typeaheadScore?: number,
      typeaheadScoringNotes?: typeahead.ScoringNotes,
      dashSearchResultType?: DashSearchResultType,
      dashSourceType: DashSourceType = "local",
    ) => {
      const {
        results,
        query,
        filters,
        featureLine,
        skeleton,
        omniboxFocusMode,
        omniboxDropdownSection,
        dashActionSurface,
      } = defaultProps;
      const resultCount = results.length;
      const activeFilters = concatActiveFilterBindings(filters);
      const hasQuery = query.length > 0;

      reportSessionAwarePapEvent(
        [SessionManagerName.Search, SessionManagerName.SearchAttempt],
        (sessionAwareness) =>
          PAP_Shown_DashSearchResult({
            resultUuid: skeleton.uuid,
            activeFilters,
            queryString: query,
            title: skeleton.proxyType,
            dashSearchResultType,
            searchRequestId: undefined,
            dashSourceType,
            resultUpdatedAtMs: undefined,
            rightRailOpened: false,
            totalScore: isTypeahead ? typeaheadScore : undefined,
            hitCount: undefined,
            resultCount,
            searchResultSource: "local_cache",
            fromUpstream: false,
            isPinned: false,
            latency: undefined,
            hasQuery,
            featureLine,
            dashActionSurface,
            actionSurfaceComponent,
            searchQueryUuid,
            // Typeahead-specific
            isTypeahead,
            typeaheadResultType,
            omniboxFocusMode,
            omniboxDropdownSection,
            ...sessionAwareness,
            ...getTypeaheadScoringNotesPAPProperties(typeaheadScoringNotes),
            ...calculatePapPositions(
              results,
              (result) => result.uuid === defaultProps.skeleton.uuid,
            ),
          }),
      );
    },
    [reportSessionAwarePapEvent, searchQueryUuid],
  );

  return {
    // Open
    logSearchResultOpen,
    logRecommendationOpen,
    logURLShortcutOpen,
    logStackResultOpen,
    logStackItemResultOpen,
    logPreviousQueryOpen,
    logMathCalculationOpen,
    logSearchFilterOpen,
    logAskSuggestionOpen,
    logAskUserQueryOpen,
    logRecentChatOpen,
    logDraftWithSourceOpen,
    logOrganizeUserQueryOpen,
    logDraftFromTemplateOpen,
    logDraftFromToneOpen,
    logWriteUserQueryOpen,
    logPeopleResultOpen,
    logSkeletonOpen,
    // Shown
    logStackResultShown,
    logStackItemResultShown,
    logSearchResultShown,
    logRecommendationShown,
    logPreviousQueryShown,
    logMathCalculationShown,
    logDesktopFileShown,
    logDesktopApplicationShown,
    logTypeaheadResultCopy,
    logURLShortcutShown,
    logSearchFilterShown,
    logAskSuggestionShown,
    logAskUserQueryShown,
    logRecentChatShown,
    logDraftWithSourceShown,
    logOrganizeUserQueryShown,
    logDraftFromTemplateShown,
    logDraftFromToneShown,
    logWriteUserQueryShown,
    logPeopleResultShown,
    logSkeletonShown,
    // Copy
    logSearchResultCopy,
    // Misc
    logSearchResultAddedToStack_SERP,
    logSearchResultAddedToStack_Typeahead,
  };
}

export function calculatePapPositions<
  T extends DefaultProps["results"] | (TypeaheadResult | undefined)[],
>(
  results: T,
  findCb: (result: T[number]) => boolean,
): {
  resultPosition: number | undefined;
  resultPositionNoCta: number | undefined;
} {
  const resultsWithoutCta = results.filter((result) =>
    result && "type" in result // typeahead.ScoredResult
      ? result.type !== typeahead.ResultType.SuggestedQuery
      : // SearchResult
        true,
  );

  const resultIdx = results.findIndex(findCb);
  const resultIdxNoCta = resultsWithoutCta.findIndex(findCb);

  const formattedResultIdx = resultIdx >= 0 ? resultIdx : undefined;
  const formattedResultIdxNoCta =
    resultIdxNoCta >= 0 ? resultIdxNoCta : formattedResultIdx;

  return {
    resultPosition: formattedResultIdx,
    resultPositionNoCta: formattedResultIdxNoCta,
  };
}

type TypeaheadScoringNotesPAPProperties = Pick<
  NonNullable<Open_DashSearchResult["properties"]>,
  // Title Match Score
  | "typeaheadScoringTitleMatchScoreUnweightedScore"
  | "typeaheadScoringTitleMatchScoreWeightedScore"
  | "typeaheadScoringTitleMatchScoreConfiguredWeight"
  | "typeaheadScoringTitleMatchScoreAppliedWeight"
  // Last Clicked Score
  | "typeaheadScoringLastClickedScoreUnweightedScore"
  | "typeaheadScoringLastClickedScoreWeightedScore"
  | "typeaheadScoringLastClickedScoreConfiguredWeight"
  | "typeaheadScoringLastClickedScoreAppliedWeight"
  // Frequently Clicked Score
  | "typeaheadScoringFrequentlyClickedScoreUnweightedScore"
  | "typeaheadScoringFrequentlyClickedScoreWeightedScore"
  | "typeaheadScoringFrequentlyClickedScoreConfiguredWeight"
  | "typeaheadScoringFrequentlyClickedScoreAppliedWeight"
  // Last Browser Viewed Score
  | "typeaheadScoringLastBrowserViewedScoreUnweightedScore"
  | "typeaheadScoringLastBrowserViewedScoreWeightedScore"
  | "typeaheadScoringLastBrowserViewedScoreConfiguredWeight"
  | "typeaheadScoringLastBrowserViewedScoreAppliedWeight"
  // Frequently Browser Viewed Score
  | "typeaheadScoringFrequentlyBrowserViewedScoreUnweightedScore"
  | "typeaheadScoringFrequentlyBrowserViewedScoreWeightedScore"
  | "typeaheadScoringFrequentlyBrowserViewedScoreConfiguredWeight"
  | "typeaheadScoringFrequentlyBrowserViewedScoreAppliedWeight"
  // File Type Score
  | "typeaheadScoringFileTypeScoreUnweightedScore"
  | "typeaheadScoringFileTypeScoreWeightedScore"
  | "typeaheadScoringFileTypeScoreConfiguredWeight"
  | "typeaheadScoringFileTypeScoreAppliedWeight"
  // Pinned
  | "typeaheadScoringPinned"
>;

type EmptyObject = Record<string, never>;

function getTypeaheadScoringNotesPAPProperties(
  scoringNotes?: typeahead.ScoringNotes,
): TypeaheadScoringNotesPAPProperties | EmptyObject {
  if (scoringNotes === undefined) return {};
  return typeaheadScoringNotesToPAPProperties(scoringNotes);
}
// Export for testing only
export { getTypeaheadScoringNotesPAPProperties };

function typeaheadScoringNotesToPAPProperties(
  scoringNotes: typeahead.ScoringNotes,
): TypeaheadScoringNotesPAPProperties {
  const { pinned, scoringComponents } = scoringNotes;

  const {
    titleMatchScore,
    lastClickedScore,
    frequentlyClickedScore,
    lastBrowserViewedScore,
    frequentlyBrowserViewedScore,
    fileTypeScore,
  } = scoringComponents;

  // Scoring system uses `null` for non-scores, but PAP wants `undefined`
  // Use nullish coalescing operator (??) to convert
  // NOTE: `??` will NOT inadvertently convert 0 scores to undefined

  return {
    // Title Match Score
    typeaheadScoringTitleMatchScoreUnweightedScore:
      titleMatchScore.unweightedScore ?? undefined,
    typeaheadScoringTitleMatchScoreWeightedScore:
      titleMatchScore.weightedScore ?? undefined,
    typeaheadScoringTitleMatchScoreConfiguredWeight:
      titleMatchScore.configuredWeight ?? undefined,
    typeaheadScoringTitleMatchScoreAppliedWeight:
      titleMatchScore.appliedWeight ?? undefined,

    // Last Clicked Score
    typeaheadScoringLastClickedScoreUnweightedScore:
      lastClickedScore.unweightedScore ?? undefined,
    typeaheadScoringLastClickedScoreWeightedScore:
      lastClickedScore.weightedScore ?? undefined,
    typeaheadScoringLastClickedScoreConfiguredWeight:
      lastClickedScore.configuredWeight ?? undefined,
    typeaheadScoringLastClickedScoreAppliedWeight:
      lastClickedScore.appliedWeight ?? undefined,

    // Frequently Clicked Score
    typeaheadScoringFrequentlyClickedScoreUnweightedScore:
      frequentlyClickedScore.unweightedScore ?? undefined,
    typeaheadScoringFrequentlyClickedScoreWeightedScore:
      frequentlyClickedScore.weightedScore ?? undefined,
    typeaheadScoringFrequentlyClickedScoreConfiguredWeight:
      frequentlyClickedScore.configuredWeight ?? undefined,
    typeaheadScoringFrequentlyClickedScoreAppliedWeight:
      frequentlyClickedScore.appliedWeight ?? undefined,

    // Last Browser Viewed Score
    typeaheadScoringLastBrowserViewedScoreUnweightedScore:
      lastBrowserViewedScore.unweightedScore ?? undefined,
    typeaheadScoringLastBrowserViewedScoreWeightedScore:
      lastBrowserViewedScore.weightedScore ?? undefined,
    typeaheadScoringLastBrowserViewedScoreConfiguredWeight:
      lastBrowserViewedScore.configuredWeight ?? undefined,
    typeaheadScoringLastBrowserViewedScoreAppliedWeight:
      lastBrowserViewedScore.appliedWeight ?? undefined,

    // Frequently Browser Viewed Score
    typeaheadScoringFrequentlyBrowserViewedScoreUnweightedScore:
      frequentlyBrowserViewedScore.unweightedScore ?? undefined,
    typeaheadScoringFrequentlyBrowserViewedScoreWeightedScore:
      frequentlyBrowserViewedScore.weightedScore ?? undefined,
    typeaheadScoringFrequentlyBrowserViewedScoreConfiguredWeight:
      frequentlyBrowserViewedScore.configuredWeight ?? undefined,
    typeaheadScoringFrequentlyBrowserViewedScoreAppliedWeight:
      frequentlyBrowserViewedScore.appliedWeight ?? undefined,

    // File Type Score
    typeaheadScoringFileTypeScoreUnweightedScore:
      fileTypeScore.unweightedScore ?? undefined,
    typeaheadScoringFileTypeScoreWeightedScore:
      fileTypeScore.weightedScore ?? undefined,
    typeaheadScoringFileTypeScoreConfiguredWeight:
      fileTypeScore.configuredWeight ?? undefined,
    typeaheadScoringFileTypeScoreAppliedWeight:
      fileTypeScore.appliedWeight ?? undefined,

    // Pinned
    typeaheadScoringPinned: pinned,
  };
}
// Export for testing only
export { typeaheadScoringNotesToPAPProperties };
